import { HttpEventType } from '@angular/common/http';
import { Component, HostListener, inject, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { Title } from '@angular/platform-browser';
import { Router } from '@angular/router';
import moment from 'moment';
import readXlsxFile from 'read-excel-file';
import { filter, firstValueFrom, Subscription } from 'rxjs';
import { AuthenticationContants } from 'src/app/constants/authentication-constants';
import { ProjectName } from 'src/app/constants/project-name-constants';
import { RemissionStateEnums } from 'src/app/enum/remission-state-enums';
import { UserModel } from 'src/app/models/authentication/user.model';
import { RentalMovementImportModel } from 'src/app/models/imports/rental-movements-import.model';
import { LoadPreInvoicingModel } from 'src/app/models/invoicing/load-preinvoicing.models';
import { PreInvoicingAlertModel } from 'src/app/models/invoicing/pre-invoicing-alert.models';
import { ColumnResponseModel } from 'src/app/models/responses/column-response.model';
import { WindowService } from 'src/app/services/authentication/window.service';
import { UserAuthenticatedService } from 'src/app/services/global-services/user-authenticated.service';
import { HeaderService } from 'src/app/services/header/header.service';
import { BranchService } from 'src/app/services/management/branch.service';
import { CompanyService } from 'src/app/services/management/company.service';
import { RentalMovementsService } from 'src/app/services/rental/rental-movements.service';
import { ToastHandlerService } from 'src/app/services/toast/toast-handler.service';

@Component({
  selector: 'app-rental-movement-import',
  templateUrl: './rental-movement-import.component.html',
  styleUrls: ['./rental-movement-import.component.scss'],
  standalone: false
})
export class RentalMovementImportComponent implements OnInit {

  private readonly branchService = inject(BranchService);
  private readonly companyService = inject(CompanyService);
  private readonly formBuilder = inject(FormBuilder);
  private readonly headerService = inject(HeaderService);
  private readonly rentalMovementsService = inject(RentalMovementsService);
  private readonly router = inject(Router);
  private readonly title = inject(Title);
  private readonly toastHandlerService = inject(ToastHandlerService);
  private readonly userAuthenticatedService = inject(UserAuthenticatedService);
  private readonly windowService = inject(WindowService);

  branchCode: number = 0;
  branchId: number = 0;
  companyCode: number = 0;
  companyId: number = 0;
  eventFile: any;
  errorRentalMovement: number = 0;
  filters: ColumnResponseModel[] = [];
  formImport: FormGroup = new FormGroup({});
  isOpened: string | undefined = undefined;
  listBranches: any[] = [];
  listCompanies: any[] = [];
  loadPreInvoicingModel: LoadPreInvoicingModel = new LoadPreInvoicingModel();
  nameFile: string = 'Ningún archivo seleccionado.';
  nameWindow: string = 'Importación Movimientos Alquiler';
  rentalMovementAlerts: Array<PreInvoicingAlertModel> = new Array<PreInvoicingAlertModel>;
  permissionUpdatable: boolean = false;
  projectName = ProjectName.projectName;
  subscription: Subscription;
  url: string = "rental-movements-import";
  userAuthenticated: UserModel = new UserModel();
  visibleTxt: boolean = false;

  ngOnInit() {
    this.title.setTitle(`${this.nameWindow} - ${this.projectName}`);
    const filters = localStorage.getItem(this.url);
    if (filters) this.filters = JSON.parse(filters);
    this.headerService.setTitle(this.nameWindow);
    this.getPermission();
    this.validateForm();
  }

  @HostListener('window:beforeunload')
  beforeUnloadHandler() {
    this.ngOnDestroy();
  }

  async ngOnDestroy() {
    const windowsOpenedJson = localStorage.getItem(AuthenticationContants.keyWindowsOpenedLocalStorage);
    if (windowsOpenedJson) {
      const windowsOpened: Array<string> = JSON.parse(windowsOpenedJson);
      const window = windowsOpened.findIndex(x => x === this.url);
      if (window != -1) {
        windowsOpened.splice(window, 1);
        localStorage.setItem(AuthenticationContants.keyWindowsOpenedLocalStorage, JSON.stringify(windowsOpened));
      }
    }

    if (this.subscription) {
      this.subscription.unsubscribe();
    }
  }

  attach() {
    document.getElementById('fileImport')?.click();
  }

  clearInputFile() {
    this.nameFile = 'Ningún archivo seleccionado.';
    if (this.eventFile) {
      this.eventFile.target.value = '';
    }
  }

  downloadErrorsTxt() {
    if (!this.rentalMovementAlerts || this.rentalMovementAlerts.length === 0) {
      this.toastHandlerService.showToast('error', 'No hay alertas para exportar');
      return;
    }

    this.rentalMovementsService.exportImportErrors(this.rentalMovementAlerts).subscribe({
      next: (blob: Blob) => {
        const file = new Blob([blob], { type: 'text/plain' });
        const url = window.URL.createObjectURL(file);
        const today = new Date();
        const formatDate = today.toISOString().split('T')[0];

        const a = document.createElement('a');
        a.href = url;
        a.download = `ErroresImportacionMovimientosAlquiler_${formatDate}.txt`;
        a.click();

        window.URL.revokeObjectURL(url);
      },
      error: () => {
        this.toastHandlerService.showToast('error', 'Error al exportar el archivo');
      }
    });
  }

  downloadGuideImport() {
    this.rentalMovementsService.downloadGuideImportRentalMovement().subscribe(data => {
      switch (data.type) {
        case HttpEventType.Response: {
          const downloadedFile = new Blob([data.body!], { type: data.body!.type });
          const a = document.createElement('a');
          a.setAttribute('style', 'display:none;');
          document.body.appendChild(a);
          a.download = "GuiaMovimientosAlquiler.docx";
          a.href = URL.createObjectURL(downloadedFile);
          a.target = '_blank';
          a.click();
          document.body.removeChild(a);
          break;
        }
      }
    });
  }

  getNameFile(file: any) {
    this.eventFile = file;
    const files = this.eventFile.target.files;
    if (files.length != 0) {
      for (let i = 0; i < files.length; i++) {
        this.nameFile = files[i].name;
      }
    } else {
      this.nameFile = 'Ningún archivo seleccionado.';
    }
  }

  importRentalMovement() {
    Object.keys(this.formImport.controls).forEach(key => {
      const control = this.formImport.get(key);
      if (control && control.invalid) {
        console.warn(`${key} es inválido`, control.errors, 'value:', control.value);
      }
    });
    if (this.formImport.valid) {
      let isOk: boolean = true;
      const input = document.getElementById("fileImport") as any;
      const rowsImport = new Array<RentalMovementImportModel>();
      let row: RentalMovementImportModel;
      let counter: number = 1, documentTypeCode: string, consecutive: number, alterNumber: number | null, stateCode: number, swBlock: boolean, documentDate: Date,
        invoiceDate: Date, swDontChargeInvoiceDay: boolean | null, costCenterCode: number, documentTransporter: string, vehicleCode: number, transportValue: number,
        transportValuePay: number, advertiser: string | null, printObservation: string | null, observation: string | null, orderConsecutive: number | null,
        articleCode: number, storeCode: number, quantity: number | null, clientValue: number | null, predevolutionQuantity: number | null, workshopQuantity: number | null,
        cleaningQuantity: number | null, cleaningValue: number | null, repairQuantity: number | null, repairValue: number | null, replacementQuantity: number | null,
        replacementValue: number | null;
      if (input.files[0]) {
        readXlsxFile(input.files[0]).then(async (rows) => {
          if (rows.length <= 1) {
            this.toastHandlerService.showToast('warning', `El archivo ingresado no tiene registros para importar.`);
            return;
          }
          rows.splice(0, 1);
          rows.forEach(element => {
            counter = counter + 1;
            let rowHasError = false;

            //A. Código Compañía:
            const companyCode = parseFloat(element[0] as string);
            if (isNaN(companyCode)) {
              const alert = new PreInvoicingAlertModel();
              alert.alertType = 1;
              alert.message = `Fila ${counter}. El código de la Compañía no es válido. `;
              this.rentalMovementAlerts.push(alert);
              rowHasError = true;
            } else {
              if (companyCode != this.companyCode) {
                const alert = new PreInvoicingAlertModel();
                alert.alertType = 1;
                alert.message = `Fila ${counter}. La Compañía del archivo no es igual a la Compañía seleccionada.`;
                this.rentalMovementAlerts.push(alert);
                rowHasError = true;
              }
            }

            //B. Código Sucursal:
            const branchCode = parseFloat(element[1] as string);
            if (isNaN(branchCode)) {
              const alert = new PreInvoicingAlertModel();
              alert.alertType = 1;
              alert.message = `Fila ${counter}. El código de la Sucursal no es válido. `;
              this.rentalMovementAlerts.push(alert);
              rowHasError = true;
            } else {
              if (branchCode != this.branchCode) {
                const alert = new PreInvoicingAlertModel();
                alert.alertType = 1;
                alert.message = `Fila ${counter}. La Sucursal del archivo no es igual a la Sucursal seleccionada.`;
                this.rentalMovementAlerts.push(alert);
                rowHasError = true;
              }
            }

            //C. Tipo Documento:
            documentTypeCode = element[2] as string;
            if (documentTypeCode == "" || documentTypeCode == null) {
              const alert = new PreInvoicingAlertModel();
              alert.alertType = 1;
              alert.message = `Fila ${counter}. El código del tipo de documento no es válido. `;
              this.rentalMovementAlerts.push(alert);
              rowHasError = true;
            } else if (element[2] as string != "R" && element[2] as string != "P" && element[2] as string != "D") {
              const alert = new PreInvoicingAlertModel();
              alert.alertType = 1;
              alert.message = `Fila ${counter}. El código del tipo de documento no es válido. `;
              this.rentalMovementAlerts.push(alert);
              rowHasError = true;
            }

            //D. Consecutivo:
            consecutive = parseFloat(element[3] as string);
            if (isNaN(consecutive)) {
              const alert = new PreInvoicingAlertModel();
              alert.alertType = 1;
              alert.message = `Fila ${counter}. El consecutivo no es válido. `;
              this.rentalMovementAlerts.push(alert);
              rowHasError = true;
            }

            //E. Número Alterno:
            alterNumber = parseFloat(element[4] as string);

            //F. Estado:
            stateCode = parseFloat(element[5] as string);
            if (isNaN(stateCode)) {
              const alert = new PreInvoicingAlertModel();
              alert.alertType = 1;
              alert.message = `Fila ${counter}. El estado no es válido. `;
              this.rentalMovementAlerts.push(alert);
              rowHasError = true;
            } else if (stateCode != RemissionStateEnums.open && stateCode != RemissionStateEnums.finalClose && stateCode != RemissionStateEnums.annulled && stateCode != RemissionStateEnums.printed) {
              const alert = new PreInvoicingAlertModel();
              alert.alertType = 1;
              alert.message = `Fila ${counter}. El estado no es válido. (1, 2, 3, 4)`;
              this.rentalMovementAlerts.push(alert);
              rowHasError = true;
            }

            //G. Bloqueado:
            if (element[6] as string != "S" && element[6] as string != "N") {
              const alert = new PreInvoicingAlertModel();
              alert.alertType = 1;
              alert.message = `Fila ${counter}, Columna G. El campo Bloqueado debe ser valores como: S / N.`;
              this.rentalMovementAlerts.push(alert);
              rowHasError = true;
            }
            swBlock = element[6] as string == "S";

            //H. Fecha Documento:
            const documentDateString = element[7] as string;
            if (documentDateString == null || documentDateString == "") {
              const alert = new PreInvoicingAlertModel();
              alert.alertType = 1;
              alert.message = `Fila ${counter}, Columna H. El campo Fecha Documento es obligatorio.`;
              this.rentalMovementAlerts.push(alert);
              rowHasError = true;
            }

            //Convierte fecha de string a Date
            documentDate = moment(documentDateString, 'DD/MM/YYYY').toDate();
            if (!moment(documentDateString, 'DD/MM/YYYY').isValid()) {
              const alert = new PreInvoicingAlertModel();
              alert.alertType = 1;
              alert.message = `Fila ${counter}, Columna H. El campo Fecha Documento no es válido.`;
              this.rentalMovementAlerts.push(alert);
              rowHasError = true;
            }

            //I. Fecha Facturacion:
            const invoiceDateString = element[8] as string;
            if (invoiceDateString == null || invoiceDateString == "") {
              const alert = new PreInvoicingAlertModel();
              alert.alertType = 1;
              alert.message = `Fila ${counter}, Columna I. El campo Fecha Facturación es obligatorio.`;
              this.rentalMovementAlerts.push(alert);
              rowHasError = true;
            }

            //Convierte fecha de string a Date
            invoiceDate = moment(invoiceDateString, 'DD/MM/YYYY').toDate();

            if (!moment(invoiceDateString, 'DD/MM/YYYY').isValid()) {
              const alert = new PreInvoicingAlertModel();
              alert.alertType = 1;
              alert.message = `Fila ${counter}, Columna I. El campo Fecha Facturación no es válido.`;
              this.rentalMovementAlerts.push(alert);
              rowHasError = true;
            }

            //J. Cobro día Facturación:
            const valSwDontCharge = element[9] ? String(element[9]).toUpperCase().trim() : null;
            if (documentTypeCode === "D" || documentTypeCode === "P") {
              // Si es Devolución o Predevolución, es obligatorio y debe ser S o N
              if (valSwDontCharge !== "S" && valSwDontCharge !== "N") {
                const alert = new PreInvoicingAlertModel();
                alert.alertType = 1;
                alert.message = `Fila ${counter}, Columna J. El campo Cobro día Facturación debe ser S o N.`;
                this.rentalMovementAlerts.push(alert);
                rowHasError = true;
              }
              swDontChargeInvoiceDay = valSwDontCharge === "S";
            }

            //K. Centro de Costos:
            costCenterCode = parseFloat(element[10] as string);
            if (isNaN(costCenterCode)) {
              const alert = new PreInvoicingAlertModel();
              alert.alertType = 1;
              alert.message = `Fila ${counter}, Columna K. El campo Centro de Costos no es válido.`;
              this.rentalMovementAlerts.push(alert);
              rowHasError = true;
            }

            //L. Documento Transportador:
            documentTransporter = element[11] as string;
            if (documentTransporter == null) {
              const alert = new PreInvoicingAlertModel();
              alert.alertType = 1;
              alert.message = `Fila ${counter}, Columna L. El campo Documento Transportador es obligatorio.`;
              this.rentalMovementAlerts.push(alert);
              rowHasError = true;
            } else if (documentTransporter.length > 15) {
              const alert = new PreInvoicingAlertModel();
              alert.alertType = 1;
              alert.message = `Fila ${counter}, Columna L. El campo Documento Transportador supera el tamaño máximo de caracteres (15).`;
              this.rentalMovementAlerts.push(alert);
              rowHasError = true;
            }

            //M. Código Vehículo:
            vehicleCode = parseFloat(element[12] as string);
            if (isNaN(vehicleCode)) {
              const alert = new PreInvoicingAlertModel();
              alert.alertType = 1;
              alert.message = `Fila ${counter}, Columna M. El campo Código Vehículo no es válido.`;
              this.rentalMovementAlerts.push(alert);
              rowHasError = true;
            }

            //N. Valor Transporte a Cobrar:
            transportValue = parseFloat(element[13] as string);
            if (isNaN(transportValue)) {
              const alert = new PreInvoicingAlertModel();
              alert.alertType = 1;
              alert.message = `Fila ${counter}, Columna N. El campo Valor Transporte a Cobrar no es válido.`;
              this.rentalMovementAlerts.push(alert);
              rowHasError = true;
            }

            //O. Valor Transporte a Pagar:
            transportValuePay = parseFloat(element[14] as string);
            if (isNaN(transportValuePay)) {
              const alert = new PreInvoicingAlertModel();
              alert.alertType = 1;
              alert.message = `Fila ${counter}, Columna O. El campo Valor Transporte a Pagar no es válido.`;
              this.rentalMovementAlerts.push(alert);
              rowHasError = true;
            }

            //P. Anunciante:
            advertiser = element[15] as string;
            if (documentTypeCode == "P") {
              if (advertiser == null || advertiser == "") {
                const alert = new PreInvoicingAlertModel();
                alert.alertType = 1;
                alert.message = `Fila ${counter}, Columna P. El campo Anunciante no es válido.`;
                this.rentalMovementAlerts.push(alert);
                rowHasError = true;
              } else if (advertiser != null && advertiser.length > 50) {
                const alert = new PreInvoicingAlertModel();
                alert.alertType = 1;
                alert.message = `Fila ${counter}, Columna P. El campo Anunciante supera el tamaño máximo de caracteres (50).`;
                this.rentalMovementAlerts.push(alert);
                rowHasError = true;
              }
            }

            //Q. Observación Impresión:
            printObservation = element[16] as string;
            if (printObservation != null && printObservation.length > 250) {
              const alert = new PreInvoicingAlertModel();
              alert.alertType = 1;
              alert.message = `Fila ${counter}, Columna Q. El campo Observación Impresión supera el tamaño máximo de caracteres (250).`;
              this.rentalMovementAlerts.push(alert);
              rowHasError = true;
            }

            //R. Observación:
            observation = element[17] as string;
            if (observation != null && observation.length > 250) {
              const alert = new PreInvoicingAlertModel();
              alert.alertType = 1;
              alert.message = `Fila ${counter}, Columna R. El campo Observación supera el tamaño máximo de caracteres (250).`;
              this.rentalMovementAlerts.push(alert);
              rowHasError = true;
            }

            //S. Consecutivo Pedido:
            orderConsecutive = parseFloat(element[18] as string);

            //T. Código Artículo:
            articleCode = parseFloat(element[19] as string);
            if (isNaN(articleCode)) {
              const alert = new PreInvoicingAlertModel();
              alert.alertType = 1;
              alert.message = `Fila ${counter}, Columna T. El campo Código Artículo no es válido.`;
              this.rentalMovementAlerts.push(alert);
              rowHasError = true;
            }

            //U. Código Bodega:
            storeCode = parseFloat(element[20] as string);
            if (isNaN(storeCode)) {
              const alert = new PreInvoicingAlertModel();
              alert.alertType = 1;
              alert.message = `Fila ${counter}, Columna U. El campo Código Bodega no es válido.`;
              this.rentalMovementAlerts.push(alert);
              rowHasError = true;
            }

            //V. Cantidad:
            quantity = parseFloat(element[21] as string);
            if (documentTypeCode == "R") {
              if (isNaN(quantity)) {
                const alert = new PreInvoicingAlertModel();
                alert.alertType = 1;
                alert.message = `Fila ${counter}, Columna V. El campo Cantidad no es válido.`;
                this.rentalMovementAlerts.push(alert);
                rowHasError = true;
              }
            }

            //W. Valor Cliente:
            clientValue = parseFloat(element[22] as string);
            if (documentTypeCode == "R") {
              if (isNaN(clientValue)) {
                const alert = new PreInvoicingAlertModel();
                alert.alertType = 1;
                alert.message = `Fila ${counter}, Columna W. El campo Valor Cliente no es válido.`;
                this.rentalMovementAlerts.push(alert);
                rowHasError = true;
              }
            }

            //X. Cantidad Predevolución:
            predevolutionQuantity = parseFloat(element[23] as string);
            if (documentTypeCode == "P") {
              if (isNaN(predevolutionQuantity)) {
                const alert = new PreInvoicingAlertModel();
                alert.alertType = 1;
                alert.message = `Fila ${counter}, Columna X. El campo Cantidad Predevolución no es válido.`;
                this.rentalMovementAlerts.push(alert);
                rowHasError = true;
              }
            }

            //Y. Cantidad Almacén:
            workshopQuantity = parseFloat(element[24] as string);
            if (documentTypeCode == "D") {
              if (isNaN(workshopQuantity)) {
                const alert = new PreInvoicingAlertModel();
                alert.alertType = 1;
                alert.message = `Fila ${counter}, Columna Y. El campo Cantidad Almacén no es válido.`;
                this.rentalMovementAlerts.push(alert);
                rowHasError = true;
              }
            }

            //Z. Cantidad Limpieza:
            cleaningQuantity = parseFloat(element[25] as string);
            if (documentTypeCode == "D") {
              if (isNaN(cleaningQuantity)) {
                const alert = new PreInvoicingAlertModel();
                alert.alertType = 1;
                alert.message = `Fila ${counter}, Columna Z. El campo Cantidad Limpieza no es válido.`;
                this.rentalMovementAlerts.push(alert);
                rowHasError = true;
              }
            }

            //AA. Valor Limpieza:
            cleaningValue = parseFloat(element[26] as string);
            if (documentTypeCode == "D") {
              if (isNaN(cleaningValue)) {
                const alert = new PreInvoicingAlertModel();
                alert.alertType = 1;
                alert.message = `Fila ${counter}, Columna AA. El campo Valor Limpieza no es válido.`;
                this.rentalMovementAlerts.push(alert);
                rowHasError = true;
              }
            }

            //AB. Cantidad Reparación:
            repairQuantity = parseFloat(element[27] as string);
            if (documentTypeCode == "D") {
              if (isNaN(repairQuantity)) {
                const alert = new PreInvoicingAlertModel();
                alert.alertType = 1;
                alert.message = `Fila ${counter}, Columna AB. El campo Cantidad Reparación no es válido.`;
                this.rentalMovementAlerts.push(alert);
                rowHasError = true;
              }
            }

            //AC. Valor Reparación:
            repairValue = parseFloat(element[28] as string);
            if (documentTypeCode == "D") {
              if (isNaN(repairValue)) {
                const alert = new PreInvoicingAlertModel();
                alert.alertType = 1;
                alert.message = `Fila ${counter}, Columna AC. El campo Valor Reparación no es válido.`;
                this.rentalMovementAlerts.push(alert);
                rowHasError = true;
              }
            }

            //AD. Cantidad Faltante:
            replacementQuantity = parseFloat(element[29] as string);
            if (documentTypeCode == "D") {
              if (isNaN(replacementQuantity)) {
                const alert = new PreInvoicingAlertModel();
                alert.alertType = 1;
                alert.message = `Fila ${counter}, Columna AD. El campo Cantidad Faltante no es válido.`;
                this.rentalMovementAlerts.push(alert);
                rowHasError = true;
              }
            }

            //AE. Valor Faltante:
            replacementValue = parseFloat(element[30] as string);
            if (documentTypeCode == "D") {
              if (isNaN(replacementValue)) {
                const alert = new PreInvoicingAlertModel();
                alert.alertType = 1;
                alert.message = `Fila ${counter}, Columna AE. El campo Valor Faltante no es válido.`;
                this.rentalMovementAlerts.push(alert);
                rowHasError = true;
              }
            }

            if (!rowHasError) {
              row = new RentalMovementImportModel(counter, this.companyId, this.branchId, documentTypeCode, consecutive, alterNumber, stateCode, swBlock, documentDate,
                invoiceDate, swDontChargeInvoiceDay, costCenterCode, documentTransporter, vehicleCode, transportValue, transportValuePay, advertiser, printObservation,
                observation, orderConsecutive, articleCode, storeCode, quantity, clientValue, predevolutionQuantity, workshopQuantity, cleaningQuantity, cleaningValue, repairQuantity,
                repairValue, replacementQuantity, replacementValue);
              rowsImport.push(row);
            } else {
              isOk = false;
            }
          });
          if (isOk && rowsImport.length > 0) {
            try {
              const response = await firstValueFrom(this.rentalMovementsService.importRentalMovement(rowsImport));
              this.loadPreInvoicingModel = response;
              this.rentalMovementAlerts = response?.alerts ?? [];
              this.errorRentalMovement = response?.preInvoicingStateId;

              if (this.errorRentalMovement === 1) {
                this.visibleTxt = true;
                this.clearInputFile();
                this.nameFile = 'Ningún archivo seleccionado.';
                this.toastHandlerService.showToast(
                  'error',
                  'La importación terminó con errores, puede exportarlos si lo desea.',
                  'ERROR');
              } else if (this.rentalMovementAlerts.length > 0) {
                this.visibleTxt = true;
                this.toastHandlerService.showToast('error', 'La importación terminó con alertas, puede exportarlas si lo desea');
              } else {
                this.visibleTxt = false;
                this.toastHandlerService.showToast('success', `La importación terminó exitosamente.`);
              }
              this.clearInputFile();
            } catch (error) {
              this.nameFile = 'Ningún archivo seleccionado.';
              console.error('Error al importar los movimientos.', error);
            }
            (<HTMLInputElement>document.getElementById('fileImport')).value = '';
          } else {
            this.errorRentalMovement = 1;
            this.visibleTxt = true;
            this.clearInputFile();
            this.nameFile = 'Ningún archivo seleccionado.';
            this.toastHandlerService.showToast(
              'error',
              'La importación terminó con errores, puede exportarlos si lo desea',
              'error');
          }
        });
      }
    } else {
      this.toastHandlerService.showToast('error', 'No ha seleccionado ningún archivo.');
    }
  }

  async getAllLists(...listsToLoad: Array<'companies' | 'branches'>) {
    try {
      const lists = {
        companies: async () => {
          this.listCompanies = await firstValueFrom(this.companyService.getAllCompaniesForSelectAsync());
        },
        branches: async () => {
          this.listBranches = await firstValueFrom(this.branchService.GetAllBranchesByCompanyIdForSelectAsync(this.companyId));
        }
      };
      if (listsToLoad.length) {
        await Promise.all(listsToLoad.map(list => lists[list]()));
      } else {
        await Promise.all([
          lists.branches(),
          lists.companies()
        ]);
      }
    } catch (error) {
      console.error('Error obtener las listas.', error);
    }
  }

  async getBranch(branchId: number | null) {
    if (!branchId) {
      this.branchId = 0;
      this.branchCode = 0;
      return;
    }

    const branch = this.listBranches.find(x => x.value == branchId);
    this.branchId = branchId;
    this.branchCode = branch.code;
  }

  async getCompany(companyId: number | null) {
    if (!companyId) {
      this.companyId = 0;
      this.companyCode = 0;
      this.listBranches = [];
      this.validateForm();
      return;
    }

    const company = this.listCompanies.find(x => x.value == companyId);
    this.companyId = companyId;
    this.companyCode = company.code;
    await this.getAllLists('branches');
  }

  async getPermission() {
    let url = this.router.url as string
    url = url.substring(1);
    this.url = url;
    const windowsOpenedJson = localStorage.getItem(AuthenticationContants.keyWindowsOpenedLocalStorage);
    if (windowsOpenedJson) {
      const windowsOpened: Array<string> = JSON.parse(windowsOpenedJson);
      this.isOpened = windowsOpened.find(x => x === this.url);
      windowsOpened.push(this.url);
      localStorage.setItem(AuthenticationContants.keyWindowsOpenedLocalStorage, JSON.stringify(windowsOpened));
      if (this.isOpened) {
        this.toastHandlerService.showToast(
          'error',
          `No se permite tener dos ventanas/pestañas de ${this.nameWindow} abiertas.`
        );
      }
    } else {
      localStorage.clear();
    }
    try {
      this.permissionUpdatable = await firstValueFrom(this.windowService.getPermissionProfileByUrl(this.url));
    } catch (error) {
      console.error('Error al buscar los permisos por tipo de impuesto.', error);
    }
    await Promise.all([
      this.getAllLists('companies'),
      this.getPermissionsForUserByWindow(),
    ])
  }

  async getPermissionsForUserByWindow() {
    this.userAuthenticated = await firstValueFrom(
      this.userAuthenticatedService.userObservable.pipe(
        filter(user => user && user.userId !== 0)
      )
    );
  }

  showValidationMessages() {
    const controlMessages: Record<string, Record<string, string>> = {
      companyId: {
        required: 'La compañía es obligatoria.',
        min: 'La compañía es obligatiria.',
      },
      branchId: {
        required: 'La sucursal es obligatoria.',
        min: 'La sucursal es obligatiria.'
      }
    };

    Object.keys(controlMessages).forEach(controlName => {
      const control = this.formImport.get(controlName);
      if (control && control.invalid && control.errors) {
        const errors = control.errors;
        Object.keys(errors).forEach(errorKey => {
          const message = controlMessages[controlName][errorKey];
          if (message) {
            this.toastHandlerService.showToast('warning', message, 'Atención');
          }
        });
      }
    });
  }

  validateForm() {
    this.formImport = this.formBuilder.group({
      companyId: [0, [Validators.required, Validators.min(1)]],
      branchId: [0, [Validators.required, Validators.min(1)]],
    });
  }
}